import React, {useEffect, useState} from "react";
import {landlordService} from "../../services/landlordService";
import {authService, useAuth} from "../../services/authService";
import {useLocation, useParams} from "react-router-dom/cjs/react-router-dom";
import {Form, Tab, Tabs} from "react-bootstrap";
import Visits from "../visits/Visits";
import PageWrapper from "../components/PageWrapper";
import Spinner from "../shared/Spinner";
import Reservations from "../reservations/Reservations";
import LandlordEditForm from "../components/LandlordEditForm";
import LandlordIntegrationForm from "../components/LandlordIntegrationForm";
import {HybridIntegrationReportPanel} from "../components/HybridIntegrationReport";
import {getLandlordLevel, getLandlordLevelLabel} from "../components/TopLandlordIcon";
import LandlordLevelPanel from "../components/LandlordLevelPanel";
import cogoToast from "cogo-toast";
import defaultAvatar from "../../assets/images/avatar.png";
import Properties from "../properties/Properties";
import {useDialogContext} from "../App";
import {useTranslation} from "react-i18next";
import moment from 'moment';
import {setCachedNotes} from "./landlordNotesCache";

const useQuery = () => {
    let searchParams = {};
    new URLSearchParams(useLocation().search).forEach((value, key) => {
        searchParams[key] = value;
    });
    return searchParams;
};

const LandlordInformation = () => {
    const {setModal} = useDialogContext();
    const {t} = useTranslation();
    let {user} = useAuth();

    let {landlord_id} = useParams();

    const query = useQuery();
    const [landlord, setLandlord] = useState(null);
    const [acceptedR, setAcceptedR] = useState(null);
    const [confirmedR, setConfirmedR] = useState(null);
    const [error, setError] = useState(null);

    const getLandlordVisits = (landlord_id) => async () => {
        return await landlordService.getLandlordVisits(landlord_id)
    }

    const getLandlordProperties = (landlord_id) => async () => {
        return {properties: await landlordService.getLandlordProperties(landlord_id)}
    }

    const getLandlordReservations = (landlord_id) => async () => {
        return  await landlordService.getLandlordReservations(landlord_id)
    }

    useEffect( () => {
        landlordService.getLandlordReservations(landlord_id, 10000).then((res) => {
            setAcceptedR(res.reservations?.filter((reservation) => reservation.acceptedDate)?.length);
            setConfirmedR(res.reservations?.filter((reservation) => reservation.acceptedDate && reservation.payment.status === "paid" && !reservation.rejectedDate && !reservation.cancellation)?.length);
        })
    }, [landlord_id]);


    const editLandlordModal = async () => {
        let _editLandlord={};
        setModal({
            title: landlord.name+"'s information",
            size: "lg",
            message: <LandlordEditForm editLandlord={landlord} setEditLandlord={(cb)=>_editLandlord = cb(_editLandlord)} validateIban={handleValidateIban}/> ,
            onSubmit: async () => {
                let response = await landlordService.editLandlord(landlord_id, _editLandlord);
                setModal(m => ({...m, hide: true}));
                if(response.error) {
                    cogoToast.error(response.error, {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Error'
                    })
                } else {
                    if (_editLandlord.hasOwnProperty("notes")) setCachedNotes(landlord_id, _editLandlord.notes);
                    setLandlord(prevLandlord => {
                        let editedLandlord = {...prevLandlord, ..._editLandlord};
                        Object.keys(_editLandlord).forEach(field => {
                            if (_editLandlord[field] === "$_REMOVE_$") delete editedLandlord[field];
                        });
                        // sem comissao manual o backend volta ao valor por omissao, que e o que o elastic passa a ter
                        if (_editLandlord.hasOwnProperty("commission")) {
                            editedLandlord.customCommission = _editLandlord.commission !== "$_REMOVE_$";
                            if (!editedLandlord.customCommission) editedLandlord.commission = prevLandlord.defaultCommission;
                        }
                        return editedLandlord;
                    });
                    cogoToast.info('Landlord edited successfully!', {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Success'
                    });
                }
            },
        })
    };

    const impersonateLandlord = async () => {
        let token = await authService.impersonate("landlord", landlord.id);
        window.open(process.env.REACT_APP_LANDLORD_URL + "/backoffice/impersonate?type=landlord&token=" + token, '_blank').focus();
    }

    const advancedEditListings = () => {
        window.location.href += "/listings";
    }

    const addLandlordVivinIntegration = async () => {
        await landlordService.addLandlordVivinIntegration(landlord_id);
        setLandlord(prevLandlord => {
            return {
                ...prevLandlord,
                "vivin_integration": true
            }
        });
    }

    const integrationErrors = {
        "invalid_integration_url": "Invalid integration URL.",
        "integration_url_unreachable": "Could not fetch listings from the integration URL.",
        "integration_url_invalid_response": "The integration URL did not return a list of listings.",
        "landlord_has_active_properties": "The landlord has active properties. Please confirm that they will be disabled.",
        "integration_no_valid_listings": "No listing in the feed passes the required checks.",
        "integration_has_invalid_listings": "Some listings fail the required checks. Please confirm that they won't be published."
    };

    const addLandlordHybridIntegration = async (integrationUrl, disableProperties, skipInvalid) => {
        try {
            let response = await landlordService.addLandlordHybridIntegration(landlord_id, integrationUrl, disableProperties, skipInvalid);
            setModal(m => ({...m, hide: true}));
            setLandlord(prevLandlord => {
                let editedLandlord = {
                    ...prevLandlord,
                    "integration_url": integrationUrl,
                    "integration_landlord": true
                };
                delete editedLandlord.vivin_integration;
                return editedLandlord;
            });
            cogoToast.info(`Full Vivin integration activated: ${response.validListings} of ${response.integrationProperties} listings will be published` +
                (response.invalidListings > 0 ? `, ${response.invalidListings} fail the checks` : "") +
                `. ${response.disabledProperties} regular properties were disabled.`, {
                hideAfter: 8,
                position: 'top-center',
                heading: 'Success'
            });
        } catch (e) {
            let message = e.error?.message || e.validationErrors?.[0]?.msg || e.message;
            cogoToast.error(integrationErrors[message] || message || "Something went wrong", {
                hideAfter: 6,
                position: 'top-center',
                heading: 'Error'
            });
        }
    }

    const integrationModal = () => {
        let _integration = {};
        setModal({
            title: "Activate Integration",
            size: "xl",
            message: <LandlordIntegrationForm landlord={landlord} landlordId={landlord_id} errorMessages={integrationErrors}
                                              onChange={(values) => _integration = values}/>,
            onSubmit: async () => {
                if (_integration.type === "vivin") {
                    await addLandlordVivinIntegration();
                    setModal(m => ({...m, hide: true}));
                    return;
                }
                if (!_integration.integrationUrl) {
                    return cogoToast.error("Please insert the integration URL.", {hideAfter: 5, position: 'top-center', heading: 'Error'});
                }
                if (!_integration.report) {
                    return cogoToast.error("Please check the feed before activating.", {hideAfter: 5, position: 'top-center', heading: 'Error'});
                }
                if (_integration.report.valid === 0) {
                    return cogoToast.error(integrationErrors["integration_no_valid_listings"], {hideAfter: 5, position: 'top-center', heading: 'Error'});
                }
                if (_integration.report.invalid > 0 && !_integration.confirmSkipInvalid) {
                    return cogoToast.error(integrationErrors["integration_has_invalid_listings"], {hideAfter: 5, position: 'top-center', heading: 'Error'});
                }
                if (_integration.propertiesCount > 0 && !_integration.confirmDisable) {
                    return cogoToast.error(integrationErrors["landlord_has_active_properties"], {hideAfter: 5, position: 'top-center', heading: 'Error'});
                }
                await addLandlordHybridIntegration(_integration.integrationUrl, _integration.confirmDisable, _integration.report.invalid > 0);
            }
        });
    }

    const integrationReportModal = () => {
        setModal({
            title: "Full Vivin feed report",
            size: "xl",
            message: <HybridIntegrationReportPanel landlordId={landlord_id} errorMessages={integrationErrors}/>
        });
    }

    const [loadingSheetsIntegration, setLoadingSheetsIntegration] = useState(false);

    const fetchAndOpenSheetsIntegration = async () => {
        setLoadingSheetsIntegration(true);
        try {
            let spreadsheetId = await landlordService.getSheetsIntegration(landlord_id);
            setLoadingSheetsIntegration(false);
            if (spreadsheetId) {
                window.open(`https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=0`, '_blank').focus();
            }
        } catch (e) {
            setLoadingSheetsIntegration(false);
            cogoToast.error(e.error.message || e.error, {
                hideAfter: 5,
                position: 'top-center',
                heading: 'Error integrating with Google Sheets.'
            });
        }
    }

    const sheetsIntegration = async () => {
        if(loadingSheetsIntegration) return;
        if(landlord.sheets_integration) return fetchAndOpenSheetsIntegration();

        setModal({
            title: "Google Sheets Integration",
            size: "md",
            message: <div>This landlord ({landlord.name}) does <strong>NOT</strong> have an active integration.<br/>Please
                confirm that you want to integrate with Google Sheets for this landlord.</div>,
            onSubmit: async () => {
                await fetchAndOpenSheetsIntegration();
                setModal(m => ({...m, hide: true}));
            }
        });
    }

    useEffect(() => {
        (async ()=>{
            try{
                let _landlord = await landlordService.getLandlordById(landlord_id);
                setLandlord(_landlord);
                console.log(_landlord);
                if(query.validateIban) {
                    //17fdf014-f4e6-43fb-a219-4866954014dd
                    let bankInfoId = query.validateIban;
                    let bankInfo = (_landlord?.multipleBankInfo || [])?.find(info => info.id === bankInfoId);
                    if(bankInfo?.iban && !bankInfo.valid) {
                        handleValidateIban(_landlord, bankInfo);
                    }
                }
                if(query.verifyPhoto) {
                    handlePhotoVerification();
                }
            } catch (e) {
                console.log(e);
                setError(e);
            }
        })();
    }, []);

    const validateIban = async (landlord, bankInfo) => {
        try {
            let response = await landlordService.validateIban(landlord.id, bankInfo.id, bankInfo.iban);
            setLandlord(prevLandlord => {
                return {
                    ...prevLandlord,
                    "multipleBankInfo": (prevLandlord.multipleBankInfo || []).map(info => info.id === bankInfo.id ? {...info, "valid": true} : info)
                }
            });
            setModal(m => ({...m, hide: true}));
            if(response.error) {
                cogoToast.error(response.error, {
                    hideAfter: 5,
                    position: 'top-center',
                    heading: 'Something went wrong!'
                });
            } else {
                cogoToast.info('Landlord iban validated!', {
                    hideAfter: 5,
                    position: 'top-center',
                    heading: 'Success'
                });
            }
        } catch(e) {
            console.log(e)
            let errorMessage = typeof e === "object" ? e.message || e.error : "Something went wrong"
            cogoToast.info(errorMessage, {
                hideAfter: 5,
                position: 'top-center',
                heading: 'Error'
            });
        }
    }
    const handleValidateIban = (landlord, bankInfo) => {
        if(!bankInfo?.iban || bankInfo?.valid) return;
        setModal({
            title: landlord.name + "'s new iban",
            size: "lg",
            message: "Validate new iban: " + bankInfo.iban,
            onSubmit: () => validateIban(landlord, bankInfo),
        })
    }

    const landlordPhoto = landlord?.photo || defaultAvatar;

    const handlePhotoVerification = () => {
        if(!landlord?.photo || landlord?.photo_verified) return;
        setModal({
            title: landlord.name+"'s profile photo",
            size: "sm",
            message: <div>Do you want to verify {landlord.name}'s photo?</div>,
            onSubmit: async () => {
                let response = await landlordService.verifyPhoto(landlord_id);
                setModal(m => ({...m, hide: true}));
                if(response.error) {
                    cogoToast.error(response.error, {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Error'
                    })
                } else {
                    setLandlord(prevLandlord => {
                        return {
                            ...prevLandlord,
                            "photo_verified": true
                        }
                    });
                    cogoToast.info('Landlord photo verified!', {
                        hideAfter: 5,
                        position: 'top-center',
                        heading: 'Success'
                    });
                }
            },
        })
    }

    const LoadingDots = () => {
        return (
            <div className="dot-opacity-loader" style={{"width": "100%", "height": "22px", "transform": "translate(7px, -26px)"}}>
                <span style={{"margin": "2px, 2px", "height": "5px", "width": "5px"}}/>
                <span style={{"margin": "2px, 2px", "height": "5px", "width": "5px"}}/>
                <span style={{"margin": "2px, 2px", "height": "5px", "width": "5px"}}/>
            </div>
        )
    }

    const [loadingRemovePenalty, setLoadingRemovePenalty] = useState(false);
    const handleRemovePenalty = async () => {
        setLoadingRemovePenalty(true);
        let response = await landlordService.removePenalty(landlord.id);
        if(response.error) {
            cogoToast.error(response.error, {
                hideAfter: 5,
                position: 'top-center',
                heading: 'Error'
            })
        } else {
            setLandlord(prevLandlord => {
                return {
                    ...prevLandlord,
                    "penalty": null
                }
            });
            cogoToast.info('Penalty removed successfully!', {
                hideAfter: 5,
                position: 'top-center',
                heading: 'Success'
            });
        }
        setLoadingRemovePenalty(false);
    }

    const handleChangePrePaidBookings = async () => {
        let prepaid_bookings = !landlord.prepaid_bookings;
        setLandlord(prevLandlord => {
            return {
                ...prevLandlord,
                "prepaid_bookings": !prevLandlord.prepaid_bookings
            }
        });
        await landlordService.editLandlord(landlord.id, {"prepaid_bookings": prepaid_bookings});
    }

    const defaultFirst = (infos) => [...(infos || [])].sort((a, b) => (b.default ? 1 : 0) - (a.default ? 1 : 0));

    const countryName = (country) => {
        if(!country) return "N/A";
        let key = "country." + country.toLowerCase();
        let name = t(key);
        return name === key ? country.toUpperCase() : name;
    }

    const infoEntryHeader = (title, isDefault, status) => {
        return (
            <div className={"d-flex align-items-center flex-wrap mb-1"} style={{fontSize: "0.875rem"}}>
                <span className={"text-dark font-weight-bold mr-2"}>{title}</span>
                {isDefault ? <span className="badge badge-secondary text-dark mr-2">Default</span> : ""}
                {status}
            </div>
        )
    }

    const fiscalInfoCard = () => {
        const fiscalInfos = defaultFirst(landlord?.multipleFiscalInfo);
        return (
            <div className={"card LandlordInformation_info-card"}>
                <div className={"card-body"}>
                    <h6 className={"card-title"}>Fiscal Info</h6>
                    <div className={"LandlordInformation_info-list"}>
                        {fiscalInfos.length === 0 ?
                            <p className={"text-muted mb-0"}>No fiscal info recorded</p> :
                            fiscalInfos.map((fiscalInfo, index) => (
                                <div key={fiscalInfo.id || "fiscalInfo-" + index}
                                     className={index > 0 ? "mt-3 pt-3 border-top" : ""}>
                                    {infoEntryHeader("Fiscal Info " + (index + 1), fiscalInfo.default,
                                        fiscalInfo.vatValidated ? <span style={{color: "#2DC928"}}>VAT Validated</span> : "")}
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-domain icon-md-1 mr-2 text-muted"/>
                                        {fiscalInfo.fiscalName || "N/A"}
                                    </p>
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-file-document-edit-outline icon-md-1 mr-2 text-muted"/>
                                        {fiscalInfo.vat || "N/A"}
                                    </p>
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-map-marker-outline icon-md-1 mr-2 text-muted"/>
                                        {fiscalInfo.address || "N/A"}
                                    </p>
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-flag-outline icon-md-1 mr-2 text-muted"/>
                                        {countryName(fiscalInfo.country)}
                                    </p>
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        )
    }

    const bankInfoCard = () => {
        const bankInfos = defaultFirst(landlord?.multipleBankInfo);
        return (
            <div className={"card LandlordInformation_info-card"}>
                <div className={"card-body"}>
                    <h6 className={"card-title"}>Bank Info</h6>
                    <div className={"LandlordInformation_info-list"}>
                        {bankInfos.length === 0 ?
                            <p className={"text-muted mb-0"}>No bank info recorded</p> :
                            bankInfos.map((bankInfo, index) => (
                                <div key={bankInfo.id || "bankInfo-" + index}
                                     className={index > 0 ? "mt-3 pt-3 border-top" : ""}>
                                    {infoEntryHeader("Bank Info " + (index + 1), bankInfo.default, <>
                                        {(bankInfo.iban && bankInfo.valid) ?
                                            <span style={{color: "#2DC928"}}>
                                                Validated (by {bankInfo.validatedBy || "undefined"} on {(bankInfo.validatedOn && moment(bankInfo.validatedOn).format("DD/MM/YYYY")) || "undefined"})
                                            </span> : ""}
                                        {(bankInfo.iban && !bankInfo.valid) ?
                                            <span style={{color: "#f3454e"}}>Not validated</span> : ""}
                                    </>)}
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-cash icon-md-1 mr-2 text-muted"/>
                                        {bankInfo.iban || "N/A"}
                                    </p>
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-account-outline icon-md-1 mr-2 text-muted"/>
                                        {bankInfo.fiscalName || "N/A"}
                                    </p>
                                    <p className="align-items-center d-flex mb-0">
                                        <i className="mdi mdi-flag-outline icon-md-1 mr-2 text-muted"/>
                                        {countryName(bankInfo.country)}
                                    </p>
                                    {(bankInfo.iban && !bankInfo.valid) ?
                                        <div className={"d-flex align-items-center flex-wrap mt-2"}>
                                            {user?.permissions?.includes("landlords_edit_bank") ?
                                                <button className={"btn btn-outline-primary btn-true-sm mr-2"}
                                                        onClick={() => handleValidateIban(landlord, bankInfo)}>Validate IBAN</button> : ""}
                                            {bankInfo.proofDocumentUrl ?
                                                <a href={bankInfo.proofDocumentUrl} target={"_blank"} rel={"noopener noreferrer"}>See proof document</a> : ""}
                                        </div> : ""}
                                </div>
                            ))}
                    </div>
                </div>
            </div>
        )
    }

    return (
        (landlord ?
                <div className={"LandlordInformation"}>
                    <PageWrapper title={"Landlord's Page"}
                                 breadcrumbs={[
                                     {url: "landlords", title: "Landlords"},
                                     {title: landlord?.name}
                                 ]}
                                 noCard setModal={setModal}>
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="LandlordInformation_header row">
                                            <div className="col-4">
                                                {getLandlordLevel(landlord) !== "none" &&
                                                    <span className="badge badge-secondary text-dark mr-3">{getLandlordLevelLabel(landlord)}</span>}
                                                {landlord.landlordLevelOverride &&
                                                    <span className="badge badge-info text-dark mr-3">Level pinned manually</span>}
                                                {landlord.promotion?.endDate && moment(landlord.promotion.endDate).isAfter(moment()) && <span className="badge badge-warning text-dark">Has Promotion</span>}
                                                <div className={"LandlordInformation_topstats mt-3"}>
                                                    <div>
                                                        <p className="number text-dark mb-0">{landlord.activeListings || 0}</p>
                                                        <p className="label text-muted">Active Listings</p>
                                                    </div>
                                                    <div>
                                                        <p className="number text-dark mb-0">{landlord.answerRatio}%</p>
                                                        <p className="label text-muted">Response Rate</p>
                                                    </div>
                                                    <div>
                                                        {(confirmedR || confirmedR === 0) ?
                                                            <p className="number text-dark mb-0">{confirmedR}</p> :
                                                            <LoadingDots/>}
                                                        <p className="label text-muted">Confirmed Bookings</p>
                                                    </div>
                                                    <div>
                                                        {(acceptedR || acceptedR === 0) ?
                                                            <p className="number text-dark mb-0">{acceptedR}</p>:
                                                            <LoadingDots/>}
                                                        <p className="label text-muted">Accepted Bookings</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-4">
                                                <div style={{backgroundImage: 'url(' + landlordPhoto + ')'}}
                                                     className="rounded-circle avatar">
                                                    <i className={"mdi mdi-check-circle " + (landlord?.photo && !landlord.photo_verified ? 'clickable' : '')} onClick={handlePhotoVerification} style={{color: landlord?.photo_verified ? "#2DC928" : "#6F6F6F"}}/>
                                                </div>
                                                <div className="LandlordInformation_header-info mb-2">
                                                    <h3 className="title text-capitalize">{landlord.name}</h3>
                                                    <p className="mb-0 d-flex align-items-center no-wrap">
                                                        <i className="mdi mdi-email-outline mr-2"/>
                                                        {landlord.email}
                                                    </p>
                                                    <p className={"d-flex align-items-center no-wrap"}>
                                                        <i className="mdi mdi-phone text-info mr-2"/>
                                                        {(landlord.dialCode ? landlord.dialCode + " " : "") + landlord.phone}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="col-4 d-flex flex-column p-0">
                                                <div className={"d-flex flex-1 justify-content-end"}>
                                                    <button className="btn btn-outline-primary btn-true-sm mr-2" onClick={editLandlordModal}>Edit Landlord</button>
                                                    <button className="btn btn-primary btn-true-sm" onClick={impersonateLandlord}>Impersonate</button>
                                                </div>
                                                <div className={"d-flex flex-1 justify-content-end mt-2"}>
                                                    <button className={"btn btn-secondary btn-true-sm" + (loadingSheetsIntegration ? " btn-loading" : "")}
                                                            disabled={loadingSheetsIntegration}
                                                            onClick={sheetsIntegration}>
                                                        Sheets Integration
                                                    </button>
                                                </div>
                                                <div className={"d-flex flex-1 justify-content-end mt-2"}>
                                                    <button className={"btn btn-outline-primary btn-true-sm"} onClick={advancedEditListings}>
                                                        Advanced Edit Listings
                                                    </button>
                                                </div>
                                                <div className={"d-flex flex-1 justify-content-end mt-2"}>
                                                    {landlord.integration_url ?
                                                      <>
                                                          <i className="mdi mdi-check text-info mr-2"/>
                                                          <span className={"mr-2"} title={landlord.integration_url}>Full Vivin</span>
                                                          <button className={"btn btn-outline-primary btn-true-sm"} onClick={integrationReportModal}>
                                                              Feed Report
                                                          </button>
                                                      </>
                                                    : <>
                                                        {landlord.vivin_integration && <>
                                                            <i className="mdi mdi-check text-info mr-2"/>
                                                            <span className={"mr-2"}>Vivin</span>
                                                        </>}
                                                        <button className={"btn btn-outline-primary btn-true-sm"} onClick={integrationModal}>
                                                            {landlord.vivin_integration ? "Change Vivin Integration" : "Activate Vivin Integration"}
                                                        </button>
                                                    </>}
                                                </div>
                                                <div className={"d-flex flex-1 justify-content-end mt-2"}>
                                                    <Form.Group className={"check_box"}>
                                                        <label htmlFor="prePaidBookings">Pre-Paid Bookings Only</label>
                                                        <Form.Control type="checkbox" className="form-control" id="prePaidBookings"
                                                                      name="prepaid_bookings" defaultChecked={!!landlord.prepaid_bookings}
                                                                      onClick={() => {
                                                                          handleChangePrePaidBookings()
                                                                      }}/>
                                                    </Form.Group>
                                                </div>
                                            </div>
                                        </div>
                                        <div className={"LandlordInformation_body row"}>
                                            <div className={"col-12"}>
                                              <div className={"card LandlordInformation_info-card"}>
                                                <div className={"card-body"}>
                                                    <h6 className={"card-title"}>General Info</h6>
                                                    <p className="align-items-center d-flex mb-0">
                                                        <i className="mdi mdi-office-building icon-md-1 mr-2 text-muted"/>
                                                        {landlord?.address || "N/A"}
                                                    </p>
                                                    <span className="align-items-center d-flex flex-wrap mb-0" style={{fontSize: "0.875rem"}}>
                                                        <i className="mdi mdi-file-document-edit-outline icon-md-1 mr-2 text-muted"/>
                                                        Commission:&nbsp;
                                                        <span style={{textDecoration: landlord.promotion?.commission && moment(landlord.promotion.endDate).isAfter(moment()) ? "line-through" : ""}}>{landlord.noCommission ? "0%" : (landlord.commission ? landlord.commission + "%" : "To be defined")}</span>
                                                        <span className={"ml-1"}>{landlord.noCommission ? "" : (landlord.firstRentCommission ? "- Over first rent" : "- Over contract of stay")}</span>
                                                        <span>{landlord.promotion?.commission && moment(landlord.promotion.endDate).isAfter(moment()) ?
                                                            <><b className={"ml-1"}>Promo: </b>{landlord.promotion.commission + "% until " + moment(landlord.promotion.endDate).format("DD/MM/YYYY")}</> : ""}</span>
                                                        {landlord.penalty ? <span className={"ml-1"} style={{color:"red"}}>(Penalty 2%, next {landlord.penalty} bookings)</span> : ""}
                                                        {(user?.permissions?.includes("landlords_remove_penalty") && landlord.penalty) ?
                                                            <button
                                                                className={"btn btn-true-sm btn-outline-danger ml-1" + (loadingRemovePenalty ? " btn-loading" : "")}
                                                                disabled={loadingRemovePenalty}
                                                                onClick={handleRemovePenalty}>Remove Penalty</button>
                                                        : ""}
                                                    </span>
                                                    <p className="align-items-center d-flex mb-0">
                                                        <i className="mdi mdi-note-text icon-md-1 mr-2 text-muted"/>
                                                        Notes: {landlord.notes || "No notes recorded"}
                                                    </p>
                                                </div>
                                              </div>
                                            </div>
                                            <div className={"col-6 mt-3"}>
                                                {fiscalInfoCard()}
                                            </div>
                                            <div className={"col-6 mt-3"}>
                                                {bankInfoCard()}
                                            </div>
                                            <div className={"col-12 mt-3"}>
                                                <LandlordLevelPanel landlord={landlord}/>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </PageWrapper>
                    <div className="LandlordInformation_tabs row mt-3">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-body">
                                    <Tabs mountOnEnter defaultActiveKey="home" id="uncontrolled-tab-example">
                                        <Tab eventKey="properties" title="Listings" className="test-tab">
                                            <Properties landlord={landlord} overrideFetch={getLandlordProperties(landlord_id)} inline cardTableStyles={"mt-6"}/>
                                        </Tab>
                                        <Tab eventKey="reservations" title="Reservations">
                                            <Reservations overrideFetch={getLandlordReservations(landlord_id)} inline cardTableStyles={"mt-6"}/>
                                        </Tab>
                                        <Tab eventKey="visits" title="Visits">
                                            <Visits overrideFetch={getLandlordVisits(landlord_id)} inline cardTableStyles={"mt-6"}/>
                                        </Tab>
                                    </Tabs>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                :
                <div className="row">
                    <div className="col-12">
                        <div className="card">
                            <div className="card-body">
                                {error ? <div style={{display: "flex", justifyContent:"center"}}>Something went wrong</div> : <Spinner inline/>}
                            </div>
                        </div>
                    </div>
                </div>
        )
    )
}

export default LandlordInformation;
